/**
 * frontend/src/lib/autoAssignToast.js
 * ═══════════════════════════════════════════════════════════════
 * Mar 2026 — UAT-AUTO-ASSIGN-MISLEADING-SUCCESS
 *
 * Single source of truth for the toast we show after the operator
 * clicks "Run Auto Assignment". Lives outside the component so
 * we can unit-test every branch without rendering the dashboard.
 *
 * Inputs:
 *   • `runResult`  — server response from POST /auto-assign/{team}/run.
 *                    Shape: { success, orders_processed, orders_assigned,
 *                             errors: [string] }
 *   • `summary`    — current dashboard summary (queue / capacity / health).
 *                    The toast-decision uses ONLY the fields the cards
 *                    already render, so the messaging never goes stale
 *                    relative to what the operator is looking at.
 *
 * Output (consumed by handleTriggerAssignment in
 * AutoAssignmentDashboard.jsx):
 *   { kind: 'success' | 'error' | 'warning' | 'info',
 *     message: string,
 *     description?: string }
 *
 * Decision tree (in order):
 *   1. run.success === false OR errors.length > 0  → 'error'
 *   2. orders_assigned > 0                          → 'success'
 *   3. pending_orders === 0                         → 'info'   ("queue is empty")
 *   4. enabled_processors === 0                     → 'warning' ("no active processors")
 *   5. total_capacity === 0                         → 'warning' ("no capacity left",
 *                                                                + stuck count if any)
 *   6. health.is_healthy === false                  → 'warning' ("system degraded",
 *                                                                + stuck/inactive counts)
 *   7. otherwise                                    → 'info'   ("considered but no
 *                                                                eligible match")
 */

function _toNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function _plural(n, singular, pluralForm) {
  return n === 1 ? singular : (pluralForm || singular + 's');
}

export function buildAutoAssignToast(runResult, summary) {
  const result = runResult || {};
  const assigned = _toNum(result.orders_assigned);
  const processed = _toNum(result.orders_processed);
  const errors = Array.isArray(result.errors) ? result.errors : [];

  // 1. Hard failures from the backend.
  if (result.success === false || errors.length > 0) {
    const reason = errors[0] || 'See run history for details.';
    // AUTOASSIGN-USABILITY-001 (minor): if the server attached processor
    // diagnostics, surface the breakdown even on hard failure.
    const pdErr = (result.diagnostics && result.diagnostics.processors) || {};
    const errBreakdown = [];
    if (typeof pdErr.enabled_total === 'number') errBreakdown.push(`Enabled: ${pdErr.enabled_total}`);
    if (typeof pdErr.effectively_active === 'number') errBreakdown.push(`Active: ${pdErr.effectively_active}`);
    if (typeof pdErr.missing_eligible_states === 'number' && pdErr.missing_eligible_states > 0)
      errBreakdown.push(`Missing eligible states: ${pdErr.missing_eligible_states}`);
    if (typeof pdErr.with_capacity === 'number') errBreakdown.push(`Engine-usable: ${pdErr.with_capacity}`);
    return {
      kind: 'error',
      message: `Auto-assignment failed: ${reason}`,
      description: errBreakdown.length ? errBreakdown.join(' · ') : undefined,
    };
  }

  // 2. Real success — at least one order assigned. When the run carries
  // diagnostics (QUEUE-STARVATION-FIX Jul 2026) the description reports
  // evaluated/total pending, exclusion breakdown, and the safety cap.
  if (assigned > 0) {
    const d = result.diagnostics || {};
    const bits = [];
    if (typeof d.orders_evaluated === 'number' && typeof d.eligible_orders === 'number')
      bits.push(`Evaluated ${d.orders_evaluated} of ${d.eligible_orders} pending`);
    if (Array.isArray(d.exclusion_reasons) && d.exclusion_reasons.length)
      bits.push(d.exclusion_reasons.slice(0, 3).map((r) => `${r.count} — ${r.label}`).join('; '));
    if (d.safety_cap_reached)
      bits.push('Safety cap (5,000 orders/run) reached — run again to continue');
    return {
      kind: 'success',
      message: `Auto-assignment complete: ${assigned} ${_plural(assigned, 'order')} assigned`,
      description: bits.length ? bits.join(' · ') : undefined,
    };
  }

  // 2.5 — Jun 2026 exclusion transparency. When the server returns a
  // diagnostics payload we use it VERBATIM: it explains exactly why each
  // unassigned order was excluded, so we never have to guess from the
  // (possibly stale) dashboard summary.
  const diag = result.diagnostics;
  if (diag && typeof diag.total_unassigned === 'number') {
    const totalUn = _toNum(diag.total_unassigned);
    const eligible = _toNum(diag.eligible_orders);
    const evaluated = typeof diag.orders_evaluated === 'number' ? _toNum(diag.orders_evaluated) : eligible;
    const capNote = diag.safety_cap_reached
      ? ' Safety cap (5,000 orders/run) reached — run again to continue.'
      : '';
    const reasons = Array.isArray(diag.exclusion_reasons) ? diag.exclusion_reasons : [];
    const reasonStr = reasons
      .slice(0, 3)
      .map((r) => `${r.count} — ${r.label}`)
      .join('; ');

    // AUTOASSIGN-USABILITY-001 (Jul 2026) — when the real cause is the
    // processor pool (not order eligibility), lead with the processor
    // breakdown so the operator never sees a misleading state/product
    // mismatch reason while zero processors are actually usable.
    const pd = diag.processors || {};
    const procBreakdown = [];
    if (typeof pd.enabled_total === 'number') procBreakdown.push(`Enabled: ${pd.enabled_total}`);
    if (typeof pd.effectively_active === 'number') procBreakdown.push(`Active: ${pd.effectively_active}`);
    if (pd.excluded_by_status && Object.keys(pd.excluded_by_status).length) {
      const s = Object.entries(pd.excluded_by_status)
        .map(([k, v]) => `${v} ${String(k).replace(/_/g, ' ')}`).join(', ');
      procBreakdown.push(`Unavailable: ${s}`);
    }
    if (typeof pd.skipped_stuck === 'number' && pd.skipped_stuck > 0) procBreakdown.push(`Stuck: ${pd.skipped_stuck}`);
    if (typeof pd.missing_eligible_states === 'number' && pd.missing_eligible_states > 0)
      procBreakdown.push(`Missing eligible states: ${pd.missing_eligible_states}`);
    if (typeof pd.with_capacity === 'number') procBreakdown.push(`Engine-usable: ${pd.with_capacity}`);
    const procStr = procBreakdown.join(' · ');

    const zeroUsable = typeof pd.with_capacity === 'number' && pd.with_capacity === 0;
    const errsFirst = Array.isArray(result.errors) && result.errors.length ? result.errors[0] : '';

    if (totalUn === 0) {
      return {
        kind: 'info',
        message: 'No pending orders to assign.',
        description: 'The queue is genuinely empty — there are no unassigned open orders for any team.',
      };
    }
    if (eligible === 0) {
      return {
        kind: 'info',
        message: `No eligible orders for this team: ${totalUn} unassigned ${_plural(totalUn, 'order')} exist, 0 eligible.`,
        description: reasonStr || 'All unassigned orders are outside this team\'s workflows.',
      };
    }
    if (zeroUsable) {
      return {
        kind: 'warning',
        message: `Auto-assignment ran: 0 assigned — evaluated ${evaluated} of ${eligible} pending ${_plural(eligible, 'order')} — no usable processors.`,
        description: (procStr || errsFirst || 'No enabled processor is active with eligible states and capacity.') + capNote,
      };
    }
    return {
      kind: 'warning',
      message: `Auto-assignment ran: 0 assigned — evaluated ${evaluated} of ${eligible} pending ${_plural(eligible, 'order')}.`,
      description: (procStr ? procStr + '. ' : '')
        + (reasonStr || 'See the Last Run breakdown on the dashboard for details.')
        + capNote,
    };
  }

  // 3 onward — 0 assigned. Pick the right contextual message.
  const pending = _toNum(summary && summary.queue && summary.queue.pending_orders);
  const capacity = _toNum(summary && summary.capacity && summary.capacity.total_capacity);
  const enabled = _toNum(summary && summary.capacity && summary.capacity.enabled_processors);
  const isHealthy = !(summary && summary.health && summary.health.is_healthy === false);
  const stuck = _toNum(summary && summary.health && summary.health.stuck_processor_count);
  const inactive = _toNum(summary && summary.health && summary.health.inactive_processor_count);

  if (pending === 0) {
    return {
      kind: 'info',
      message: 'No pending orders to assign.',
      description: 'The queue is empty — nothing for auto-assignment to do.',
    };
  }

  if (enabled === 0) {
    return {
      kind: 'warning',
      message: 'No active processors available.',
      description:
        `${pending} ${_plural(pending, 'order')} pending, but 0 processors are enabled for auto-assignment. `
        + 'Enable processors from the Processor Workloads table to receive assignments.',
    };
  }

  if (capacity === 0) {
    return {
      kind: 'warning',
      message: 'No available capacity.',
      description:
        `${pending} ${_plural(pending, 'order')} pending, but all enabled processors are at WIP cap. `
        + (stuck > 0 ? `${stuck} stuck ${_plural(stuck, 'processor')} are blocking the queue — ` : '')
        + 'Free up capacity (resolve stuck WIP or raise caps) and retry.',
    };
  }

  if (!isHealthy) {
    const reasonBits = [];
    if (stuck > 0) reasonBits.push(`${stuck} stuck`);
    if (inactive > 0) reasonBits.push(`${inactive} inactive`);
    const reasonStr = reasonBits.length > 0
      ? `Processors flagged: ${reasonBits.join(', ')}.`
      : 'System health is degraded.';
    return {
      kind: 'warning',
      message: 'Auto-assignment ran but nothing was assigned.',
      description: `${reasonStr} Review the System Health card and Processor Workloads table.`,
    };
  }

  // Healthy + capacity + pending, yet 0 assigned. Likely an
  // eligibility miss (scope / state / client / product /
  // locked orders). Surface as info, NOT success.
  return {
    kind: 'info',
    message: `No matches: ${processed} ${_plural(processed, 'order')} considered, 0 assigned.`,
    description:
      "Check that processors' assignment scope (state / client / product) overlaps the pending queue.",
  };
}
